import i18next from 'i18next';
import { all, put, call, delay, race, take, takeEvery } from 'redux-saga/effects';
import { PapiStudioBookmarkAdd, PapiStudioBookmarkRemove } from '@wix/da-papi-types';
import { isApiError, requestPuppy } from '@wix/da-http-client';
import {
  normalizeGallectionsMixed,
  normalizeGallections,
} from '@wix/da-shared-react/pkg/redux/normalizr/helpers';
import { putEntities } from '@wix/da-shared-react/pkg/redux/entities/actions';
import {
  addErrorNotification,
  addNotification,
} from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import { NotificationDuration } from '@wix/da-shared-react/pkg/Notifications/Notification';
import {
  insertItemsAtOffsets,
  deleteItem,
} from '@wix/da-shared-react/pkg/Stream/withOffset/actions';
import { addBookmark, removeBookmark, undoAddBookmark } from '../actions';

function* handleAddBookmark({ payload: { gallection } }: ReturnType<typeof addBookmark>) {
  const item_type = gallection.type;
  if (!['gallery', 'stash'].includes(item_type)) {
    yield put(
      addErrorNotification(i18next.t('studio.bookmarks.remove.error'), NotificationDuration.S)
    );
    return;
  }
  const itemid = gallection.folderId;
  const response: PapiStudioBookmarkAdd = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'bookmark/add',
      data: { item_type, itemid },
    },
    undefined,
    'studio'
  );
  if (!response || isApiError(response)) {
    let errorMessage = i18next.t('studio.bookmarks.add.error.generic');
    if (response && (response as { errorCode?: number }).errorCode === 1) {
      errorMessage = i18next.t('studio.bookmarks.add.error.maxLimit');
    }
    yield put(addErrorNotification(errorMessage, NotificationDuration.S));
    return;
  }

  // Add item to the bookmarks stream
  // 1. normalize the gallection
  gallection.isBookmarked = true;
  const normalizedMixedGallections = normalizeGallectionsMixed([gallection]);
  // 2. add it to mixed gallections redux
  yield put(putEntities({ entities: normalizedMixedGallections.entities }));
  // 3. add it to the bookmarks stream
  yield put(
    insertItemsAtOffsets({
      streamId: 'STUDIO_BOOKMARKS_STREAM',
      newItems: normalizedMixedGallections.result.map((id, index) => ({
        item: id,
        offset: index,
      })),
    })
  );

  // Also normalize the gallection in non-mixed store
  const normalizedGallections = normalizeGallections([gallection]);
  yield put(putEntities({ entities: normalizedGallections.entities }));

  // Show notification
  yield put(
    addNotification('BOOKMARK_ADDED', {
      gallection,
    })
  );

  // TODO: we might also need to update the non-mixed normalized gallection,
  // for places that use that version

  // Wait for the undo action
  const { undo } = yield race({
    undo: take(
      undoAction =>
        undoAction.type === undoAddBookmark.toString() &&
        undoAction.payload &&
        undoAction.payload.gallection.folderId === itemid
    ),
    wait: delay(5000),
  });

  if (undo) {
    yield put(removeBookmark(gallection));
  }
}

function* handleRemoveBookmark({ payload: { gallection } }: ReturnType<typeof removeBookmark>) {
  const item_type = gallection.type;
  if (!['gallery', 'stash'].includes(item_type)) {
    yield put(
      addErrorNotification(i18next.t('studio.bookmarks.remove.error'), NotificationDuration.S)
    );
    return;
  }
  const itemid = gallection.folderId;
  const response: PapiStudioBookmarkRemove = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'bookmark/remove',
      data: { item_type, itemid },
    },
    undefined,
    'studio'
  );
  if (!response || isApiError(response)) {
    yield put(
      addErrorNotification(i18next.t('studio.bookmarks.remove.error'), NotificationDuration.S)
    );
    return;
  }

  // Remove item to the bookmarks stream
  // 1. normalize the gallection
  gallection.isBookmarked = false;
  const normalizedMixedGallections = normalizeGallectionsMixed([gallection]);
  yield put(putEntities({ entities: normalizedMixedGallections.entities }));
  const entityId = normalizedMixedGallections.result.pop();
  if (!entityId) {
    return;
  }
  // 2. remove it to the bookmarks stream
  yield put(
    deleteItem({
      streamId: 'STUDIO_BOOKMARKS_STREAM',
      item: entityId,
    })
  );

  // Also normalize the gallection in non-mixed store
  const normalizedGallections = normalizeGallections([gallection]);
  yield put(putEntities({ entities: normalizedGallections.entities }));
}

export default function* bookmarksSaga() {
  yield all([
    takeEvery(addBookmark, handleAddBookmark),
    takeEvery(removeBookmark, handleRemoveBookmark),
  ]);
}
